import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { updateTermsMetadata } from '../../utils/seo';

const TermsOfService: React.FC = () => {
  useEffect(() => { updateTermsMetadata(); }, []);

  return (
    <Layout maxWidth="max-w-3xl">
      <div>
          <h1 className="page-title mb-6">Terms of Service</h1>

          <div className="ui-prose max-w-none text-ink-secondary">
            <p className="text-sm text-ink-secondary mb-8">Last updated: March 20, 2026</p>

            <p className="mb-6">
              Please read these Terms of Service ("Terms") carefully before using Color Match at color-match.io
              (the "Service") operated by Color Match ("we," "us," or "our"). By accessing or using the Service,
              you agree to be bound by these Terms. If you disagree with any part of these Terms, you may not
              access the Service.
            </p>

            <h2 className="text-xl font-bold text-ink mt-8 mb-4">1. Acceptance of Terms</h2>
            <p>
              By accessing and using Color Match, you accept and agree to be bound by these Terms and our
              Privacy Policy. These Terms apply to all visitors, users, and others who access or use the Service.
              We reserve the right to update these Terms at any time. Continued use of the Service after any
              changes constitutes your acceptance of the new Terms.
            </p>

            <h2 className="text-xl font-bold text-ink mt-8 mb-4">2. Use License</h2>
            <p>
              Permission is granted to access and use Color Match for personal, non-commercial purposes. Under
              this license, you may not:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-4 mb-4">
              <li>Reproduce, duplicate, copy, sell, or exploit any portion of the Service for commercial purposes without express written permission from us;</li>
              <li>Modify or create derivative works based on the Service;</li>
              <li>Attempt to decompile, reverse engineer, or extract source code from any software contained in or provided by the Service;</li>
              <li>Remove any copyright, trademark, or other proprietary notices from any content on the Service;</li>
              <li>Transfer access to the Service to another person or mirror the Service on any other server.</li>
            </ul>
            <p>
              This license shall automatically terminate if you violate any of these restrictions and may be
              terminated by us at any time.
            </p>

            <h2 className="text-xl font-bold text-ink mt-8 mb-4">3. Disclaimer of Warranties</h2>
            <p>
              The Service and all content, materials, information, tools, and services provided through the
              Service are provided on an "as is" and "as available" basis without warranties of any kind,
              either express or implied, including but not limited to implied warranties of merchantability,
              fitness for a particular purpose, or non-infringement. Color Match does not warrant that:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-4 mb-4">
              <li>The Service will be uninterrupted, error-free, or free of viruses or other harmful components;</li>
              <li>The results obtained from use of the Service will be accurate, complete, or current;</li>
              <li>Any errors in the Service will be corrected;</li>
              <li>Color values, contrast ratios, or WCAG compliance assessments are guaranteed to be accurate in all circumstances.</li>
            </ul>

            <h2 className="text-xl font-bold text-ink mt-8 mb-4">4. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by applicable law, Color Match and its operators shall not be
              liable for any indirect, incidental, special, consequential, or punitive damages, including but
              not limited to loss of profits, data, use, goodwill, or other intangible losses, resulting from:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-4 mb-4">
              <li>Your access to or use of (or inability to access or use) the Service;</li>
              <li>Any conduct or content of any third party on the Service;</li>
              <li>Any content obtained from the Service;</li>
              <li>Unauthorized access, use, or alteration of your transmissions or content.</li>
            </ul>

            <h2 className="text-xl font-bold text-ink mt-8 mb-4">5. Accuracy of Materials</h2>
            <p>
              The materials and tool outputs on Color Match are provided for informational and design assistance
              purposes only. Color Match does not warrant that any materials are accurate, complete, or current.
              WCAG compliance determinations made by the Service are provided as guidance only and should be
              verified against the relevant official WCAG 2.2 requirements and tested with assistive technologies.
              Color Match may make changes to the Service at any time without notice.
            </p>

            <h2 className="text-xl font-bold text-ink mt-8 mb-4">6. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with applicable laws, without regard
              to its conflict of law provisions. Any disputes arising under or in connection with these Terms
              shall be subject to the exclusive jurisdiction of the competent courts in the applicable territory.
            </p>

            <h2 className="text-xl font-bold text-ink mt-8 mb-4">7. Advertising</h2>
            <p>Ad serving is currently paused. The site retains its AdSense account verification tag and ads.txt, which do not load advertising scripts. If advertising is enabled, the privacy notice and consent controls will be updated to reflect the services in use.</p>

            <h2 className="text-xl font-bold text-ink mt-8 mb-4">8. Intellectual Property</h2>
            <p>
              The Service, including all content, features, and functionality (including but not limited to
              all information, software, text, displays, images, and the design, selection, and arrangement
              thereof), is owned by Color Match and is protected by copyright, trademark, patent, trade secret,
              and other intellectual property laws.
            </p>
            <p className="mt-4">
              Color values, palette suggestions, and other outputs generated by the tool are provided for your
              use in your own design projects. However, the site application and educational content remain subject to their applicable rights. Referenced standards, algorithms and third-party code retain their own ownership and license terms.
            </p>
            <p className="mt-4">
              The "Color Match" name, logo, and related marks are trademarks of Color Match. You may not use
              these marks without prior written consent.
            </p>

            <h2 className="text-xl font-bold text-ink mt-8 mb-4">9. User Conduct</h2>
            <p>When using the Service, you agree not to:</p>
            <ul className="list-disc pl-5 space-y-2 mt-4 mb-4">
              <li>Use automated scripts, bots, scrapers, or similar tools to access or collect data from the Service without our express written permission;</li>
              <li>Submit excessive automated requests that place an unreasonable burden on the Service's infrastructure;</li>
              <li>Attempt to interfere with, compromise the system integrity or security of, or decipher any transmissions to or from the servers running the Service;</li>
              <li>Use the Service to transmit any unlawful, harmful, threatening, abusive, harassing, defamatory, or otherwise objectionable content;</li>
              <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity;</li>
              <li>Use the Service in any way that could disable, overburden, damage, or impair the Service.</li>
            </ul>
            <p>
              Violation of this section may result in immediate termination of your access to the Service and
              may be reported to appropriate legal authorities.
            </p>

            <h2 className="text-xl font-bold text-ink mt-8 mb-4">10. Third-Party Links and Services</h2>
            <p>
              The Service may contain links to third-party websites or services that are not owned or controlled
              by Color Match. Color Match has no control over, and assumes no responsibility for, the content,
              privacy policies, or practices of any third-party websites or services.
            </p>
            <p className="mt-4">
              The Service integrates with the following third-party services, each subject to their own terms:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-4 mb-4">
              <li><strong>Google Analytics:</strong> Used with optional analytics consent to measure pages, completed tool actions and performance. Subject to Google's Terms of Service and Privacy Policy.</li>
              <li><strong>Google AdSense:</strong> Ad serving is paused; ownership verification is retained. Subject to Google's Advertising Policies.</li>
              <li><strong>Formspree:</strong> When configured, used to process contact form submissions subject to Formspree's Terms of Service.</li>
            </ul>

            <h2 className="text-xl font-bold text-ink mt-8 mb-4">11. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. When we make changes, we will update the
              "Last updated" date at the top of this page. It is your responsibility to review these Terms
              periodically. Material changes to these Terms will be indicated by a revised "Last updated" date.
            </p>
            <p className="mt-4">
              Your continued use of the Service after any changes to these Terms constitutes your acceptance
              of the revised Terms. If you do not agree to the new Terms, please stop using the Service.
            </p>

            <h2 className="text-xl font-bold text-ink mt-8 mb-4">12. Contact Us</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <ul className="list-none pl-0 space-y-2 mt-4">
              <li>
                <strong>Email:</strong>{' '}
                <a href="/contact" className="text-brand-600 hover:underline">
                  the contact information page
                </a>
              </li>
              <li>
                <strong>Contact options:</strong>{' '}
                <Link to="/contact" className="text-brand-600 hover:underline">
                  color-match.io/contact
                </Link>
              </li>
            </ul>
          </div>
      </div>
    </Layout>
  );
};

export default TermsOfService;
